import { contactRows, contactsUnreadyMessage } from '@/lib/contactsRead';
import { useState, useEffect, useRef } from 'react';
import { Landmark, Loader2, Check, ChevronsUpDown, ExternalLink, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useCampaignStore } from '@/stores';
import {
  useFinanceSettings,
  useSaveFinanceSettings,
  useComplianceProfile,
} from '@/hooks/queries/useFinanceSettings';
import { useRulesets, useStatutoryElectionDates } from '@/hooks/queries/useFinanceRulesets';
import { useFinanceFormats } from '@/hooks/queries/useFinanceFormats';
import { useEarliestStagedDate } from '@/hooks/queries/useEarliestStagedDate';
import { useVolunteers } from '@/hooks/queries/useVolunteers';
import { useFinanceContacts } from '@/hooks/queries/useFinanceContacts';
import { useMissedDeadlines, useCreateAttestation } from '@/hooks/queries/useCommitteeFormation';
import { useCampaignRace } from '@/hooks/queries';
import { stageName } from '@/components/race-picture';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import { cn } from '@/lib/utils';
import { formatAmount } from '@/components/finance/utils';
import { OpeningBalanceModal } from '@/components/finance/opening-balance';
import { useDialogState } from '@/hooks/useDialogState';
import { isStaleWrite, stateFilerIdLabel } from '@signalvote/shared';
import { CommitteeType, ElectionDesignation } from '@signalvote/shared';
import type { FormatKind, OpeningBalanceResult } from '@signalvote/shared';
import {
  FEDERAL_CHAMBER_OPTIONS,
  JUDICIAL_TIER_OPTIONS,
  LA_OFFICE_LEVEL_OPTIONS,
  NJ_OFFICE_LEVEL_OPTIONS,
  OFFICE_LEVEL_OPTIONS,
} from './officeLevelOptions';
import { format, parseISO } from 'date-fns';
import { readyStaff } from '@/lib/staffRead';
import { readySettings, settingsUnreadyMessage } from '@/lib/financeSettingsRead';
import { answered } from '@/lib/reportsRead';
import { settled, unreadyMessage } from '@/lib/financeRemainderReads';
import { skip } from '@signalvote/data';
import { readyProfile } from '@/lib/complianceProfileRead';

// §163-278.40B–E — NC municipal reporting method. A municipal committee's reporting SCHEDULE
// is method-specific (there is no single municipal calendar), so the treasurer declares which
// of the four variants their city runs. Wire values mirror
// AppConstants.Finance.MunicipalElectionMethods; the labels/helper are treasurer-facing (statute
// citations appended so a treasurer can verify the method against the law).
type MunicipalMethodOption = { value: string; label: string; helper: string };

const MUNICIPAL_METHOD_OPTIONS: MunicipalMethodOption[] = [
  {
    value: 'partisan',
    label: 'Partisan (primary + general)',
    helper: 'A partisan primary followed by a general election. (§163-278.40B)',
  },
  {
    value: 'nonpartisan-with-primary',
    label: 'Nonpartisan with a primary',
    helper: 'A nonpartisan primary narrows the field, then a general election. (§163-278.40D)',
  },
  {
    value: 'nonpartisan-with-runoff',
    label: 'Nonpartisan election with a runoff',
    helper: 'A single election, then a runoff (second election) if no candidate wins outright. (§163-278.40C)',
  },
  {
    value: 'nonpartisan-plurality',
    label: 'Nonpartisan plurality (single election)',
    helper: 'One election; the top vote-getter wins, no primary or runoff. (§163-278.40E)',
  },
];

// ============================================================================
// Component
// ============================================================================

interface FinanceFormState {
  committeeName: string;
  // FEC-002 (D8): the parts are sent to the server; treasurerDisplayName is the
  // combobox label only (the server recomposes the persisted display cache).
  treasurerFirstName: string;
  treasurerLastName: string;
  treasurerDisplayName: string;
  rulesetCode: string;
  // ADR-022 — single office level (federal auto-derived from rulesetCode).
  officeLevel: string;
  // The federal chamber (house | senate | presidential). Empty = not recorded.
  federalChamber: string;
  // § 23-15-1021 — the judicial court tier (judicialTrial | judicialAppellate). Empty = not recorded.
  judicialOfficeTier: string;
  // §163-278.40B–E — NC municipal reporting method (shown only for an NC municipal committee).
  municipalElectionMethod: string;
  // #860 — the filing-destination email override (shown only for an email-transport jurisdiction).
  // Wins over any jurisdiction default; a county Mississippi filer supplies their Circuit Clerk here.
  filingDestinationEmail: string;
  stateFilerId: string;
  committeeFormationDate: string;
  priorFilingThroughDate: string;
  defaultFormatKind: string;
  // FEC-002: committee mailing address. committeeStreet2 is wired through state +
  // save payload but renders no input in v1 (builder ships Address → Street1; the
  // true Street1/Street2 split is a fast-follow) — kept so the wiring is complete.
  committeeStreet1: string;
  committeeStreet2: string;
  committeeCity: string;
  committeeState: string;
  committeeZip: string;
  // FEC-002: finance-authoritative candidate election state + district.
  electionState: string;
  electionDistrict: string;
  // Office identity header fields (free text; shown for stored-cycle VA + LA jurisdictions).
  officeSought: string;
  district: string;
  // LA LEADERS CVR/CVR2 filer identity (design #172 §3; shown for the LA jurisdiction only).
  financialInstitutionName: string;
  financialInstitutionStreet: string;
  financialInstitutionCity: string;
  financialInstitutionState: string;
  financialInstitutionZip: string;
  preparerName: string;
  preparerPhone: string;
  officeJurisdiction: string;
  // LA LEADERS Form 102 page-2 box 9 (CVR2 TRE) — the treasurer's own mailing address
  // (shown for the LA jurisdiction only).
  treasurerStreet1: string;
  treasurerStreet2: string;
  treasurerCity: string;
  treasurerState: string;
  treasurerZip: string;
  // FEC-002 (D5 amended): candidate identity FK + its picker label.
  candidateContactId: string;
  candidateDisplayName: string;
  primaryEnabled: boolean;
  primaryDate: string;
  generalEnabled: boolean;
  generalDate: string;
  // ADR-076 — the runoff split. A primary runoff precedes the general (already covered by the
  // recurring cutoff); a general runoff follows it (the one scheduled only after the general has
  // passed). Edited as two independent rows; each maps to null when its checkbox is off.
  primaryRunoffEnabled: boolean;
  primaryRunoffDate: string;
  generalRunoffEnabled: boolean;
  generalRunoffDate: string;
  // ADR-037 — the stored election-cycle start (VA / stored-cycle jurisdictions). Empty until
  // the treasurer confirms the suggested January 1 of the general-election year.
  electionCycleStartDate: string;
  // ADR-040 (§24.2-954) — VA legislative-session ban: special-election exemption latch +
  // treasurer-confirmed sine-die adjournment date (empty until confirmed).
  isSpecialElectionCommittee: boolean;
  legislativeSessionEndConfirmed: string;
  // NC §163-278.9(a) — the under-$1,000 Certification-of-Threshold latch (treasurer-declared).
  isThresholdCommittee: boolean;
  // LA LEADERS CVR col 18 — the open-vs-party-primary classification (shown for the LA jurisdiction
  // only). Tri-state as a string so the picker can express "not chosen yet": '' = unset (required
  // before an LA report can generate), 'true' = closed party primary, 'false' = open (jungle) primary.
  runsClosedPartyPrimary: string;
  // Where the ruleset asks for it: '' until chosen. The candidate count is a joint committee's only.
  committeeType: CommitteeType | '';
  jointCommitteeCandidateCount: string;
  // A joint committee's answer to whether a candidate also has their own committee: '' | 'true' | 'false'.
  jointCandidateHasOwnCommittee: string;
  // The token of the settings this form was loaded from, sent back so a stale save is a 409.
  concurrencyVersion: number | null;
}

const COMMITTEE_TYPE_OPTIONS = [
  { value: CommitteeType.SingleCandidate, label: 'Single candidate committee (Form D-1)' },
  { value: CommitteeType.JointCandidates, label: 'Joint candidates committee (Form D-2)' },
];

export function FinanceSetupCard() {
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const canManageFinance = useCampaignStore((s) => s.canManage('Finance'));

  const settingsRead = useFinanceSettings();
  const existingSettings = readySettings(settingsRead);
  const settingsUnready = settingsUnreadyMessage(settingsRead);
  const settingsAnswered = settingsRead.status === 'ready';

  // #748 — the party ceilings this campaign's SAVED settings resolve to. Read from the served
  // compliance profile rather than recomputed here, so this screen and the entry gate can never
  // disagree about the same number.
  const complianceProfile = readyProfile(useComplianceProfile());
  const partyCeiling = complianceProfile?.partyLimitPerElection ?? null;
  const combinedNationalPartyCeiling =
    complianceProfile?.nationalPartyCombinedLimitPerElection ?? null;
  const rulesetsRead = useRulesets(undefined);
  const rulesets = answered(rulesetsRead);
  const rulesetsUnready = unreadyMessage(rulesetsRead, 'the jurisdictions');
  const rulesetsSettled = settled(rulesetsRead);
  const { mutate: saveSettings, isPending, failure: saveFailure } = useSaveFinanceSettings();
  const volunteers = readyStaff(useVolunteers(undefined));
  const missedDeadlinesRead = useMissedDeadlines();
  const missedDeadlines = missedDeadlinesRead.status === 'ready' ? missedDeadlinesRead.data : undefined;
  const { mutate: createAttestation } = useCreateAttestation();
  // Only seeds the formation-date default; unread, the default falls back to today.
  const earliestStagedDateRead = useEarliestStagedDate();
  const earliestStagedDate =
    earliestStagedDateRead.status === 'ready' ? earliestStagedDateRead.data.earliestDate : null;

  const [treasurerOpen, setTreasurerOpen] = useState(false);
  const [treasurerSearch, setTreasurerSearch] = useState('');
  const treasurerRef = useRef<HTMLDivElement>(null);

  // FEC-002 — candidate-contact picker (D5 amended). The candidate is a
  // FinanceContact; search is server-side via useFinanceContacts.
  const [candidateOpen, setCandidateOpen] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');
  const candidateRef = useRef<HTMLDivElement>(null);
  const candidateContactsRead = useFinanceContacts({
    pageSize: 50,
    search: candidateSearch || undefined,
  });
  const candidateContacts = contactRows(candidateContactsRead);
  const candidateContactsUnready = contactsUnreadyMessage(candidateContactsRead);

  // Close treasurer dropdown on outside click
  useEffect(() => {
    if (!treasurerOpen) return;
    const handler = (e: MouseEvent) => {
      if (treasurerRef.current && !treasurerRef.current.contains(e.target as Node)) {
        setTreasurerOpen(false);
        setTreasurerSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [treasurerOpen]);

  // Close candidate dropdown on outside click
  useEffect(() => {
    if (!candidateOpen) return;
    const handler = (e: MouseEvent) => {
      if (candidateRef.current && !candidateRef.current.contains(e.target as Node)) {
        setCandidateOpen(false);
        setCandidateSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [candidateOpen]);

  const [saveSuccess, setSaveSuccess] = useState(false);

  const [form, setForm] = useState<FinanceFormState>({
    committeeName: '',
    treasurerFirstName: '',
    treasurerLastName: '',
    treasurerDisplayName: '',
    rulesetCode: '',
    officeLevel: '',
    federalChamber: '',
    judicialOfficeTier: '',
    municipalElectionMethod: '',
    filingDestinationEmail: '',
    stateFilerId: '',
    committeeFormationDate: getCampaignTodayString(),
    priorFilingThroughDate: '',
    defaultFormatKind: '',
    committeeStreet1: '',
    committeeStreet2: '',
    committeeCity: '',
    committeeState: '',
    committeeZip: '',
    electionState: '',
    electionDistrict: '',
    officeSought: '',
    district: '',
    financialInstitutionName: '',
    financialInstitutionStreet: '',
    financialInstitutionCity: '',
    financialInstitutionState: '',
    financialInstitutionZip: '',
    preparerName: '',
    preparerPhone: '',
    officeJurisdiction: '',
    treasurerStreet1: '',
    treasurerStreet2: '',
    treasurerCity: '',
    treasurerState: '',
    treasurerZip: '',
    candidateContactId: '',
    candidateDisplayName: '',
    primaryEnabled: false,
    primaryDate: '',
    generalEnabled: true,
    generalDate: '',
    primaryRunoffEnabled: false,
    primaryRunoffDate: '',
    generalRunoffEnabled: false,
    generalRunoffDate: '',
    electionCycleStartDate: '',
    isSpecialElectionCommittee: false,
    legislativeSessionEndConfirmed: '',
    isThresholdCommittee: false,
    runsClosedPartyPrimary: '',
    committeeType: '',
    jointCommitteeCandidateCount: '',
    jointCandidateHasOwnCommittee: '',
    concurrencyVersion: null,
  });

  const [initialized, setInitialized] = useState(false);
  // Set by a 409: the next settings read carries the token the kept edits are saved on.
  const adoptsRefetchedToken = useRef(false);
  const openingBalanceDialog = useDialogState();

  // ADR-022 — the submission-format query is driven off the sole jurisdiction
  // source of truth (rulesetCode); the redundant Filing State picker is gone.
  // The suggested cycle: the year of an election date in the form, else of the campaign's stored
  // election date, else the first cycle whose statutory primary has not passed. Both suggested
  // dates come from that one year.
  const campaignToday = getCampaignTodayString();
  const todayYear = Number(campaignToday.slice(0, 4));
  const thisYearsDates = answered(
    useStatutoryElectionDates(form.rulesetCode ? { code: form.rulesetCode, year: todayYear } : skip),
  );
  const knownElectionDate =
    form.generalDate || form.primaryDate || selectedCampaign?.electionDate?.match(/^\d{4}-\d{2}-\d{2}$/)?.[0];
  const statutoryYear = knownElectionDate
    ? Number(knownElectionDate.slice(0, 4))
    : thisYearsDates?.primaryDate
      ? thisYearsDates.primaryDate >= campaignToday
        ? todayYear
        : todayYear + 1
      : null;
  const statutoryDates = answered(
    useStatutoryElectionDates(form.rulesetCode && statutoryYear !== null ? { code: form.rulesetCode, year: statutoryYear } : skip),
  );
  // Offered only; the dates count once the person saves them, since deadlines and per-election
  // totals run on saved dates alone.
  const { data: campaignRace } = useCampaignRace();
  const raceDates = campaignRace?.dates;
  const racePrimary = raceDates?.primary ?? null;
  const racePrimaryRunoff = raceDates?.primaryRunoff ?? null;
  const raceGeneral = raceDates?.general ?? null;
  const raceGeneralRunoff = raceDates?.generalRunoff ?? null;
  const differs = (raceDate: string | null, enabled: boolean, date: string) => !!raceDate && (!enabled || date !== raceDate);
  const raceDatesDiffer =
    differs(racePrimary, form.primaryEnabled, form.primaryDate) ||
    differs(racePrimaryRunoff, form.primaryRunoffEnabled, form.primaryRunoffDate) ||
    differs(raceGeneral, form.generalEnabled, form.generalDate) ||
    differs(raceGeneralRunoff, form.generalRunoffEnabled, form.generalRunoffDate);
  const raceDateList = [
    racePrimary &&
      `${format(parseISO(racePrimary), 'MMMM d, yyyy')} (${raceDates?.primaryStage ? stageName(raceDates.primaryStage) : 'primary'})`,
    racePrimaryRunoff && `${format(parseISO(racePrimaryRunoff), 'MMMM d, yyyy')} (primary runoff, if needed)`,
    raceGeneral && `${format(parseISO(raceGeneral), 'MMMM d, yyyy')} (general)`,
    raceGeneralRunoff && `${format(parseISO(raceGeneralRunoff), 'MMMM d, yyyy')} (general runoff, if needed)`,
  ].filter((words): words is string => !!words);
  const raceDateWords =
    raceDateList.length > 1 ? `${raceDateList.slice(0, -1).join(', ')} and ${raceDateList[raceDateList.length - 1]}` : raceDateList.join('');
  const fillRaceDates = () =>
    setForm((prev) => ({
      ...prev,
      ...(racePrimary && { primaryEnabled: true, primaryDate: racePrimary }),
      ...(racePrimaryRunoff && { primaryRunoffEnabled: true, primaryRunoffDate: racePrimaryRunoff }),
      ...(raceGeneral && { generalEnabled: true, generalDate: raceGeneral }),
      ...(raceGeneralRunoff && { generalRunoffEnabled: true, generalRunoffDate: raceGeneralRunoff }),
    }));
  const storedDateWarning = (designation: ElectionDesignation, value: string) =>
    existingSettings?.electionDateWarnings?.find(
      (w) => w.designation === designation && w.storedDate === value,
    )?.message;
  const primaryWarning = storedDateWarning(ElectionDesignation.Primary, form.primaryDate);
  const generalWarning = storedDateWarning(ElectionDesignation.General, form.generalDate);

  const formatStateForQuery = (form.rulesetCode || '').toUpperCase() || null;
  const availableFormats = answered(useFinanceFormats(formatStateForQuery ?? skip)) ?? [];

  // FEC-cover-only fields (committee mailing address, election state/district,
  // and the candidate-contact picker whose only submission-builder consumer is
  // the FEC builder) render only for the federal jurisdiction (ADR-022).
  const isFederalRuleset = form.rulesetCode.toUpperCase() === 'FEDERAL';

  // The ceilings below describe the chamber that is SAVED; the picker above them is form state.
  // While the two disagree, showing them would put $62,000 under a picker that now reads House —
  // the same over-promise this whole surface exists to avoid, made by the screen rather than by
  // the lookup. Saving refreshes the profile through the write set, so the figures return with the new chamber.
  const chamberCeilingsAreCurrent =
    form.federalChamber === (existingSettings?.federalChamber ?? '');
  // ADR-037 — Virginia (and any future stored-cycle jurisdiction) totals each donor over the
  // election cycle, so it stores an explicit cycle-start date. The suggested value is January 1
  // of the general-election year, which the treasurer confirms or adjusts (e.g. a governor's
  // multi-year cycle starts earlier).
  const isStoredCycleRuleset = form.rulesetCode.toUpperCase() === 'VA';
  // Design #172 D2 — Louisiana swaps the generic office-level set for its own
  // major/district/anyOther taxonomy (with statutory helper text).
  const isLouisianaRuleset = form.rulesetCode.toUpperCase() === 'LA';
  // #860 — the filing-destination email override input shows only for an EMAIL-transport jurisdiction
  // (the app emails / the treasurer self-emails the filing to a receiving-office inbox — NC, MS),
  // where an override is meaningful. Keyed on the SERVED transport axis, not a hardcoded state code,
  // so a future email-transport jurisdiction gets the input with no edit. (Portal / in-system
  // jurisdictions upload to a portal or self-transmit and have no inbox to override.)
  const filesByEmail = complianceProfile?.filingTransport === 'email';
  // The served default follows the SAVED jurisdiction and office level; once the form changes either,
  // it no longer describes where a blank field files, so it isn't shown until the next save.
  const savedJurisdictionMatchesForm =
    !!existingSettings
    && existingSettings.rulesetCode === form.rulesetCode
    && existingSettings.officeLevel === form.officeLevel;
  const defaultDestination = savedJurisdictionMatchesForm
    ? existingSettings?.jurisdictionDefaultDestinationEmail ?? null
    : null;
  const officeLevelOptions = isLouisianaRuleset
    ? LA_OFFICE_LEVEL_OPTIONS
    : form.rulesetCode.toUpperCase() === 'NJ'
      ? NJ_OFFICE_LEVEL_OPTIONS
      : OFFICE_LEVEL_OPTIONS;
  // ADR-040 (§24.2-954) — the legislative-session fundraising ban reaches only VA statewide +
  // General Assembly committees, so its two settings (special-election latch + confirmed sine-die
  // date) render only for those offices. A VA local candidate is not subject to the ban.
  const isVaSessionBanOffice =
    isStoredCycleRuleset &&
    (form.officeLevel === 'statewide' || form.officeLevel === 'legislative');
  // §163-278.40B–E — the municipal election-method picker rides only an NC municipal committee;
  // its schedule is the only one keyed off the method.
  const isNcMunicipal =
    form.rulesetCode.toUpperCase() === 'NC' && form.officeLevel === 'municipal';
  // § 23-15-1021 — the judicial court-tier picker rides only a Mississippi judicial committee.
  // Mississippi is the one jurisdiction with a judicial per-election cap, so the tier drives a real
  // limit only there (elsewhere judicial folds into the standard non-federal rules); gated on the
  // state code like the VA/NC/LA office-specific inputs above. The server clears any tier off a
  // judicial office regardless, so a non-MS judicial committee simply never sees or sends one.
  const isMsJudicial =
    form.rulesetCode.toUpperCase() === 'MS' && form.officeLevel === 'judicial';
  const code = form.rulesetCode.toUpperCase();
  const asksCommitteeType =
    rulesets?.some((r) => r.code === form.rulesetCode && r.asksCommitteeType) ?? false;
  const isJointCommittee = asksCommitteeType && form.committeeType === CommitteeType.JointCandidates;
  // Stored-cycle jurisdictions (VA, NC, LA, NJ) total each donor over the whole election cycle, so they
  // persist an explicit cycle-start anchor. A DEDICATED flag, not the VA-only isStoredCycleRuleset
  // (which also gates VA office-sought/district and the §24.2-954 session-ban) — widening that gate
  // would drag those VA-only fields onto NC/LA. NC's staggered 2/4/8-yr terms can't be derived from
  // a general date; LA's window runs from the candidacy date (R.S. 18:1483(2)(b)). Both store the
  // anchor just like VA, and so does NJ, whose election runs from the committee's first receipt or
  // expenditure (ELEC Compliance Manual for Candidates). This flag does double duty — it renders the picker AND decides whether the
  // field survives save, so omitting a stored-cycle state here silently nulls its anchor.
  const persistsCycleStartAnchor = code === 'VA' || code === 'NC' || code === 'LA' || code === 'NJ';
  // VA and NC anchor on January 1 of the year the candidate first sought the office; Louisiana
  // anchors on the candidacy date itself, which is an arbitrary day of the year. The distinction
  // drives both the help copy and whether the Jan-1 shortcut is offered — suggesting Jan 1 in LA
  // would walk the treasurer into exactly the mis-windowed aggregate this anchor exists to fix.
  const cycleStartIsJanuaryFirst = code === 'VA' || code === 'NC';
  // §163-278.9(a) — the under-$1,000 Certification-of-Threshold latch rides an NC state/county
  // candidate committee (statewide / legislative / judicial / county — the office levels that file
  // the organizational report + quarterlies/semiannuals the certification stands in for). Municipal
  // committees file under §40B–E and are out of scope.
  const isNcThresholdEligibleOffice =
    form.rulesetCode.toUpperCase() === 'NC' &&
    ['statewide', 'legislative', 'judicial', 'county'].includes(form.officeLevel);
  const suggestedCycleStart =
    cycleStartIsJanuaryFirst && form.generalEnabled && form.generalDate
      ? `${form.generalDate.slice(0, 4)}-01-01`
      : '';

  // Initialize form from existing settings or campaign data
  useEffect(() => {
    if (initialized) return;
    if (!settingsAnswered || !rulesetsSettled) return;

    if (existingSettings) {
      setForm({
        committeeName: existingSettings.committeeName ?? '',
        treasurerFirstName: existingSettings.treasurerFirstName ?? '',
        treasurerLastName: existingSettings.treasurerLastName ?? '',
        // Display the recomposed cache; a legacy free-form name with no parts
        // still shows here, and the picker overwrites parts on re-selection.
        treasurerDisplayName: existingSettings.treasurerName ?? '',
        rulesetCode: existingSettings.rulesetCode ?? '',
        officeLevel: existingSettings.officeLevel ?? '',
        federalChamber: existingSettings.federalChamber ?? '',
        judicialOfficeTier: existingSettings.judicialOfficeTier ?? '',
        municipalElectionMethod: existingSettings.municipalElectionMethod ?? '',
        filingDestinationEmail: existingSettings.filingDestinationEmail ?? '',
        stateFilerId: existingSettings.stateFilerId ?? '',
        committeeFormationDate: existingSettings.committeeFormationDate ?? getCampaignTodayString(),
        priorFilingThroughDate: existingSettings.priorFilingThroughDate ?? '',
        defaultFormatKind: existingSettings.defaultFormatKind ?? '',
        committeeStreet1: existingSettings.committeeStreet1 ?? '',
        committeeStreet2: existingSettings.committeeStreet2 ?? '',
        committeeCity: existingSettings.committeeCity ?? '',
        committeeState: existingSettings.committeeState ?? '',
        committeeZip: existingSettings.committeeZip ?? '',
        electionState: existingSettings.electionState ?? '',
        electionDistrict: existingSettings.electionDistrict ?? '',
        officeSought: existingSettings.officeSought ?? '',
        district: existingSettings.district ?? '',
        financialInstitutionName: existingSettings.financialInstitutionName ?? '',
        financialInstitutionStreet: existingSettings.financialInstitutionStreet ?? '',
        financialInstitutionCity: existingSettings.financialInstitutionCity ?? '',
        financialInstitutionState: existingSettings.financialInstitutionState ?? '',
        financialInstitutionZip: existingSettings.financialInstitutionZip ?? '',
        preparerName: existingSettings.preparerName ?? '',
        preparerPhone: existingSettings.preparerPhone ?? '',
        officeJurisdiction: existingSettings.officeJurisdiction ?? '',
        treasurerStreet1: existingSettings.treasurerStreet1 ?? '',
        treasurerStreet2: existingSettings.treasurerStreet2 ?? '',
        treasurerCity: existingSettings.treasurerCity ?? '',
        treasurerState: existingSettings.treasurerState ?? '',
        treasurerZip: existingSettings.treasurerZip ?? '',
        candidateContactId: existingSettings.candidateContactId ?? '',
        // Server resolves the candidate's display name so the picker shows it on
        // reload (the FK alone can't render a label).
        candidateDisplayName: existingSettings.candidateContactName ?? '',
        primaryEnabled: !!existingSettings.primaryDate,
        primaryDate: existingSettings.primaryDate ?? '',
        generalEnabled: !!existingSettings.generalDate,
        generalDate: existingSettings.generalDate ?? '',
        // ADR-076 — init each runoff row from its own split field (not the legacy single runoffDate).
        primaryRunoffEnabled: !!existingSettings.primaryRunoffDate,
        primaryRunoffDate: existingSettings.primaryRunoffDate ?? '',
        generalRunoffEnabled: !!existingSettings.generalRunoffDate,
        generalRunoffDate: existingSettings.generalRunoffDate ?? '',
        electionCycleStartDate: existingSettings.electionCycleStartDate ?? '',
        isSpecialElectionCommittee: existingSettings.isSpecialElectionCommittee ?? false,
        legislativeSessionEndConfirmed: existingSettings.legislativeSessionEndConfirmed ?? '',
        isThresholdCommittee: existingSettings.isThresholdCommittee ?? false,
        // Tri-state boolean → the picker's string: null/undefined = "not chosen yet".
        runsClosedPartyPrimary:
          existingSettings.runsClosedPartyPrimary === null ||
          existingSettings.runsClosedPartyPrimary === undefined
            ? ''
            : String(existingSettings.runsClosedPartyPrimary),
        committeeType: existingSettings.committeeType ?? '',
        jointCommitteeCandidateCount: existingSettings.jointCommitteeCandidateCount?.toString() ?? '',
        jointCandidateHasOwnCommittee:
          existingSettings.jointCandidateHasOwnCommittee === null ||
          existingSettings.jointCandidateHasOwnCommittee === undefined
            ? ''
            : String(existingSettings.jointCandidateHasOwnCommittee),
        concurrencyVersion: existingSettings.concurrencyVersion ?? null,
      });
      setInitialized(true);
    } else {
      const smartFormationDate = earliestStagedDate ?? getCampaignTodayString();
      setForm((prev) => ({
        ...prev,
        committeeName: selectedCampaign?.paidForBy ?? '',
        committeeFormationDate: smartFormationDate,
      }));
      setInitialized(true);
    }
  }, [existingSettings, settingsAnswered, rulesetsSettled, selectedCampaign, earliestStagedDate, initialized]);

  const refetchedToken = existingSettings?.concurrencyVersion;
  useEffect(() => {
    if (!adoptsRefetchedToken.current || refetchedToken == null) return;
    adoptsRefetchedToken.current = false;
    setForm((prev) => ({ ...prev, concurrencyVersion: refetchedToken }));
  }, [refetchedToken]);

  // The opening balance lives on the settings row, so its write bumps the token this form saves
  // on. Adopt the new token only when the form held the one that write was sent with; a form
  // that was already stale stays stale, so its save still reports the other change.
  const adoptOpeningBalanceToken = (result: OpeningBalanceResult, sentVersion: number | null) =>
    setForm((prev) =>
      prev.concurrencyVersion === sentVersion ? { ...prev, concurrencyVersion: result.concurrencyVersion } : prev,
    );

  if (!selectedCampaign || !canManageFinance) return null;

  // An empty form here would save defaults over the committee's real settings.
  if (settingsUnready) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Landmark className="h-5 w-5" />
            Finance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p role="alert" className="text-sm text-destructive">{settingsUnready}</p>
        </CardContent>
      </Card>
    );
  }

  const handleSave = () => {
    setSaveSuccess(false);
    // ADR-076 — each runoff row maps to null when its checkbox is off. The two-row form owns the split
    // fields directly; the server stores them as-is and reads both, so the legacy single runoffDate is
    // gone and there is nothing left to dual-write.
    const primaryRunoffDate =
      form.primaryRunoffEnabled && form.primaryRunoffDate ? form.primaryRunoffDate : null;
    const generalRunoffDate =
      form.generalRunoffEnabled && form.generalRunoffDate ? form.generalRunoffDate : null;
    saveSettings(
      {
        committeeName: form.committeeName,
        // FEC-002 (D8): send the parts; the server recomposes TreasurerName.
        treasurerFirstName: form.treasurerFirstName,
        treasurerLastName: form.treasurerLastName,
        rulesetCode: form.rulesetCode || null,
        // ADR-022 — coherence: the federal jurisdiction implies office level
        // "federal" (auto); a non-federal jurisdiction sends the explicit pick.
        officeLevel: isFederalRuleset ? 'federal' : (form.officeLevel || null),
        // Cleared outside federal so a jurisdiction switch can't strand a stale chamber that
        // would key a limit row no state ruleset declares.
        federalChamber: isFederalRuleset ? (form.federalChamber || null) : null,
        // § 23-15-1021 — the judicial court tier; sent only off an MS judicial office (the server
        // also clears it off any non-judicial office, matching the chamber's clear-outside-federal).
        judicialOfficeTier: isMsJudicial ? (form.judicialOfficeTier || null) : null,
        // §163-278.40B–E — the municipal method rides only an NC municipal committee; cleared for
        // every other jurisdiction/office so a switch can't strand a stale method that would key a
        // wrong schedule.
        municipalElectionMethod: isNcMunicipal ? (form.municipalElectionMethod || null) : null,
        // #860 — the filing-destination email override (empty clears; the server trim-normalizes and
        // wins it over the jurisdiction default). Sent regardless of jurisdiction so a saved override
        // survives, but the input is only shown for an email-transport jurisdiction.
        filingDestinationEmail: form.filingDestinationEmail || null,
        // FEC-011: federal committee ID (C00…) rides the unified StateFilerId,
        // the field the FEC builder reads — no separate FecId.
        stateFilerId: form.stateFilerId,
        committeeFormationDate: form.committeeFormationDate || null,
        priorFilingThroughDate: form.priorFilingThroughDate || null,
        defaultFormatKind: (form.defaultFormatKind || null) as FormatKind | null,
        // ADR-022 §4 — committee mailing address, election state/district, and the
        // candidate FK are FEC-cover-only. They're hidden for non-federal jurisdictions;
        // null them in the payload too so a federal→state switch can't persist stale
        // federal values onto a state campaign ("not collected or recorded for state").
        committeeStreet1: isFederalRuleset ? (form.committeeStreet1 || null) : null,
        committeeStreet2: isFederalRuleset ? (form.committeeStreet2 || null) : null,
        committeeCity: isFederalRuleset ? (form.committeeCity || null) : null,
        committeeState: isFederalRuleset ? (form.committeeState || null) : null,
        committeeZip: isFederalRuleset ? (form.committeeZip || null) : null,
        electionState: isFederalRuleset ? (form.electionState || null) : null,
        electionDistrict: isFederalRuleset ? (form.electionDistrict || null) : null,
        // VA COMET header identity fields — stored-cycle (VA) jurisdictions only; cleared for
        // others so a VA→other switch can't persist stale header values.
        officeSought: isStoredCycleRuleset ? (form.officeSought || null) : null,
        district: isStoredCycleRuleset ? (form.district || null) : null,
        // LA LEADERS CVR/CVR2 filer identity (design #172 §3) — LA jurisdiction only; cleared for
        // others so an LA→other switch can't persist stale filer-identity values.
        financialInstitutionName: isLouisianaRuleset ? (form.financialInstitutionName || null) : null,
        financialInstitutionStreet: isLouisianaRuleset ? (form.financialInstitutionStreet || null) : null,
        financialInstitutionCity: isLouisianaRuleset ? (form.financialInstitutionCity || null) : null,
        financialInstitutionState: isLouisianaRuleset ? (form.financialInstitutionState || null) : null,
        financialInstitutionZip: isLouisianaRuleset ? (form.financialInstitutionZip || null) : null,
        preparerName: isLouisianaRuleset ? (form.preparerName || null) : null,
        preparerPhone: isLouisianaRuleset ? (form.preparerPhone || null) : null,
        officeJurisdiction: isLouisianaRuleset ? (form.officeJurisdiction || null) : null,
        // LA Form 102 box 9 (CVR2 TRE) treasurer address — LA jurisdiction only; cleared on switch.
        treasurerStreet1: isLouisianaRuleset ? (form.treasurerStreet1 || null) : null,
        treasurerStreet2: isLouisianaRuleset ? (form.treasurerStreet2 || null) : null,
        treasurerCity: isLouisianaRuleset ? (form.treasurerCity || null) : null,
        treasurerState: isLouisianaRuleset ? (form.treasurerState || null) : null,
        treasurerZip: isLouisianaRuleset ? (form.treasurerZip || null) : null,
        candidateContactId: form.candidateContactId || null,
        primaryDate: form.primaryEnabled && form.primaryDate ? form.primaryDate : null,
        generalDate: form.generalEnabled && form.generalDate ? form.generalDate : null,
        // ADR-076 — the two split runoff fields the form owns directly. The legacy single runoffDate is
        // gone from the entity and response, so the form no longer sends it (the request's runoffDate
        // input remains only for legacy non-form callers).
        primaryRunoffDate,
        generalRunoffDate,
        runoffDate: null,
        // ADR-037 — only stored-cycle jurisdictions (VA + NC) persist a cycle-start anchor; others
        // clear it (they derive / use the calendar year). Dedicated flag, not isStoredCycleRuleset.
        electionCycleStartDate: persistsCycleStartAnchor ? (form.electionCycleStartDate || null) : null,
        // ADR-040 (§24.2-954) — the session-ban settings ride only VA statewide/legislative
        // committees; cleared for every other jurisdiction/office so a switch can't strand a stale
        // special-election latch that would silently exempt a committee from the ban.
        isSpecialElectionCommittee: isVaSessionBanOffice ? form.isSpecialElectionCommittee : false,
        legislativeSessionEndConfirmed: isVaSessionBanOffice
          ? (form.legislativeSessionEndConfirmed || null)
          : null,
        // NC §163-278.9(a) — the threshold-committee latch rides only an NC state/county candidate
        // committee; cleared for every other jurisdiction/office so a switch can't strand a stale
        // latch that would silently suppress a committee's periodic reports.
        isThresholdCommittee: isNcThresholdEligibleOffice ? form.isThresholdCommittee : false,
        // LA LEADERS CVR col 18 — the open-vs-party-primary classification. Required for LA (the
        // server 400s if unset for LA); cleared to null for every other jurisdiction so an LA→other
        // switch can't strand a stale classification. '' → null, 'true'/'false' → the boolean.
        runsClosedPartyPrimary: isLouisianaRuleset
          ? form.runsClosedPartyPrimary === ''
            ? null
            : form.runsClosedPartyPrimary === 'true'
          : null,
        committeeType: asksCommitteeType ? form.committeeType || null : null,
        jointCommitteeCandidateCount: isJointCommittee
          ? Number.parseInt(form.jointCommitteeCandidateCount, 10) || null
          : null,
        jointCandidateHasOwnCommittee:
          isJointCommittee && form.jointCandidateHasOwnCommittee !== ''
            ? form.jointCandidateHasOwnCommittee === 'true'
            : null,
        concurrencyVersion: form.concurrencyVersion,
      },
      {
        onSuccess: () => {
          setSaveSuccess(true);
          setInitialized(false); // re-init from API on next render
          setTimeout(() => setSaveSuccess(false), 3000);
        },
        onError: (error) => {
          if (isStaleWrite(error)) adoptsRefetchedToken.current = true;
        },
      },
    );
  };

  const inputClass = cn(
    'w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1',
    'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Landmark className="h-5 w-5" />
          Finance
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Committee Identity */}
        <div>
          <h3 className="text-sm font-semibold mb-3">Committee Identity</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="finance-committeeName" className="text-sm font-medium text-muted-foreground">
                Committee Name
              </label>
              <input
                id="finance-committeeName"
                type="text"
                value={form.committeeName}
                onChange={(e) => setForm((prev) => ({ ...prev, committeeName: e.target.value }))}
                placeholder="e.g. Friends of John Smith"
                className={inputClass}
              />
              <p className="text-xs text-muted-foreground mt-1">
                <a
                  href="https://www.fec.gov/help-candidates-and-committees/registering-candidate/who-can-and-cant-register/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 hover:underline"
                >
                  FEC requirement
                  <ExternalLink className="h-3 w-3" />
                </a>
                {' '}— required on all campaign materials.
              </p>
            </div>
            <div>
              <label htmlFor="finance-treasurerName" className="text-sm font-medium text-muted-foreground">
                Treasurer
              </label>
              <div className="relative mt-1" ref={treasurerRef}>
                <button
                  id="finance-treasurerName"
                  type="button"
                  role="combobox"
                  aria-label="Treasurer"
                  aria-expanded={treasurerOpen}
                  className={cn(
                    inputClass,
                    'mt-0 flex items-center justify-between text-left cursor-pointer',
                    !form.treasurerDisplayName && 'text-muted-foreground',
                  )}
                  onClick={() => setTreasurerOpen((prev) => !prev)}
                >
                  <span>{form.treasurerDisplayName || 'Select treasurer...'}</span>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                </button>
                {treasurerOpen && (
                  <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
                    <input
                      type="text"
                      value={treasurerSearch}
                      onChange={(e) => setTreasurerSearch(e.target.value)}
                      placeholder="Search team members..."
                      className="w-full border-b px-3 py-2 text-sm outline-none"
                      autoFocus
                    />
                    <ul className="max-h-48 overflow-y-auto p-1">
                      {(volunteers ?? [])
                        .filter((v) => {
                          const fullName = `${v.firstName} ${v.lastName}`.toLowerCase();
                          return fullName.includes(treasurerSearch.toLowerCase());
                        })
                        .map((v) => {
                          const fullName = `${v.firstName} ${v.lastName}`;
                          return (
                            <li key={v.userId}>
                              <button
                                type="button"
                                className={cn(
                                  'w-full text-left px-2 py-1.5 text-sm rounded-sm hover:bg-accent',
                                  form.treasurerDisplayName === fullName && 'bg-accent',
                                )}
                                onClick={() => {
                                  // D8 — the picker carries clean parts; store them
                                  // as the source of truth and the label for display.
                                  setForm((prev) => ({
                                    ...prev,
                                    treasurerFirstName: v.firstName,
                                    treasurerLastName: v.lastName,
                                    treasurerDisplayName: fullName,
                                  }));
                                  setTreasurerOpen(false);
                                  setTreasurerSearch('');
                                }}
                              >
                                <span>{fullName}</span>
                                <span className="ml-2 text-xs text-muted-foreground">{v.email}</span>
                              </button>
                            </li>
                          );
                        })}
                      {(volunteers ?? []).filter((v) =>
                        `${v.firstName} ${v.lastName}`.toLowerCase().includes(treasurerSearch.toLowerCase()),
                      ).length === 0 && (
                        <li className="px-2 py-3 text-sm text-center text-muted-foreground">
                          No team members found.
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* State / Jurisdiction (Ruleset) */}
        <div>
          <h3 className="text-sm font-semibold mb-3">Compliance Profile</h3>
          <div>
            <label htmlFor="finance-ruleset" className="text-sm font-medium text-muted-foreground">
              State / Jurisdiction
            </label>
            <select
              id="finance-ruleset"
              value={form.rulesetCode}
              // ADR-022 — office level is jurisdiction-dependent (federal auto-derives
              // "federal"; non-federal requires an explicit pick). Reset it on any
              // jurisdiction switch so a stale value (e.g. "federal" carried into a
              // state campaign) can't be saved against the new, narrower option set.
              onChange={(e) => setForm((prev) => ({ ...prev, rulesetCode: e.target.value, officeLevel: '' }))}
              className={inputClass}
            >
              <option value="">Select a jurisdiction...</option>
              {rulesets?.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.name}
                </option>
              ))}
            </select>
            {rulesetsUnready && (
              <p role="alert" className="mt-1 text-sm text-destructive">{rulesetsUnready}</p>
            )}
          </div>

          {/* Office Level (ADR-022). A federal jurisdiction auto-derives office
              level "federal" so this selector is hidden; a non-federal
              jurisdiction requires an explicit pick. */}
          {form.rulesetCode && !isFederalRuleset && (
            <div className="mt-4">
              <label htmlFor="finance-officeLevel" className="text-sm font-medium text-muted-foreground">
                Office Level
              </label>
              <select
                id="finance-officeLevel"
                value={form.officeLevel}
                onChange={(e) => setForm((prev) => ({ ...prev, officeLevel: e.target.value }))}
                className={cn(inputClass, 'max-w-xs')}
              >
                <option value="">Select an office level...</option>
                {officeLevelOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {isLouisianaRuleset ? (
                // LA's tier is a guided, statutory classification (population/parish
                // rules), so show every tier's definition beneath the picker.
                <div className="mt-1 space-y-1">
                  {LA_OFFICE_LEVEL_OPTIONS.map((o) => (
                    <p key={o.value} className="text-xs text-muted-foreground">
                      <span className="font-medium">{o.label}:</span> {o.helper}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">
                  Determines which contribution limits and reporting thresholds apply.
                </p>
              )}
            </div>
          )}

          {asksCommitteeType && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="finance-committeeType" className="text-sm font-medium text-muted-foreground">
                  Committee type
                </label>
                <select
                  id="finance-committeeType"
                  value={form.committeeType}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, committeeType: e.target.value as CommitteeType | '' }))
                  }
                  className={cn(inputClass, 'max-w-xs')}
                >
                  <option value="">Select a committee type...</option>
                  {COMMITTEE_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  The type you registered with the Election Law Enforcement Commission. A joint
                  committee may accept each candidate&apos;s limit from a donor.
                </p>
              </div>
              {isJointCommittee && (
                <div>
                  <label
                    htmlFor="finance-jointCommitteeCandidateCount"
                    className="text-sm font-medium text-muted-foreground"
                  >
                    Number of candidates
                  </label>
                  <input
                    id="finance-jointCommitteeCandidateCount"
                    type="number"
                    min={2}
                    step={1}
                    value={form.jointCommitteeCandidateCount}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, jointCommitteeCandidateCount: e.target.value }))
                    }
                    className={cn(inputClass, 'max-w-[8rem]')}
                  />
                </div>
              )}
              {isJointCommittee && (
                <div className="md:col-span-2">
                  <label
                    htmlFor="finance-jointCandidateHasOwnCommittee"
                    className="text-sm font-medium text-muted-foreground"
                  >
                    Does any candidate in this joint committee also have their own candidate committee?
                  </label>
                  <select
                    id="finance-jointCandidateHasOwnCommittee"
                    value={form.jointCandidateHasOwnCommittee}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, jointCandidateHasOwnCommittee: e.target.value }))
                    }
                    className={cn(inputClass, 'max-w-xs')}
                  >
                    <option value="">Select...</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                  {form.jointCandidateHasOwnCommittee === 'true' &&
                    complianceProfile?.jointCommitteeSharedLimitWarning && (
                      <p
                        data-testid="finance-shared-limit-warning"
                        className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800"
                      >
                        {complianceProfile.jointCommitteeSharedLimitWarning}
                      </p>
                    )}
                </div>
              )}
            </div>
          )}

          {/* § 23-15-1021 — Mississippi judicial court tier. Optional (a committee that leaves it
              unset stays uncapped), and shown only for an MS judicial office because Mississippi is
              the one jurisdiction whose judicial races carry a per-election cap. */}
          {isMsJudicial && (
            <div className="mt-4">
              <label htmlFor="finance-judicialOfficeTier" className="text-sm font-medium text-muted-foreground">
                Judicial court level
              </label>
              <select
                id="finance-judicialOfficeTier"
                value={form.judicialOfficeTier}
                onChange={(e) => setForm((prev) => ({ ...prev, judicialOfficeTier: e.target.value }))}
                className={cn(inputClass, 'max-w-xs')}
              >
                <option value="">Select a court level...</option>
                {JUDICIAL_TIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <div className="mt-1 space-y-1">
                {JUDICIAL_TIER_OPTIONS.map((o) => (
                  <p key={o.value} className="text-xs text-muted-foreground">
                    <span className="font-medium">{o.label}:</span> {o.helper}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* LA LEADERS CVR col 18 — the open-vs-party-primary classification. Required for
              Louisiana (like the office level): it decides whether reports file under Form 102's
              party-primary or open-primary section, and the report builder refuses to generate a
              party/general-side report until it is set. Shown for the LA jurisdiction only. */}
          {isLouisianaRuleset && (
            <div className="mt-4">
              <label
                htmlFor="finance-runsClosedPartyPrimary"
                className="text-sm font-medium text-muted-foreground"
              >
                Primary type
              </label>
              <select
                id="finance-runsClosedPartyPrimary"
                value={form.runsClosedPartyPrimary}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, runsClosedPartyPrimary: e.target.value }))
                }
                className={cn(inputClass, 'max-w-xs')}
              >
                <option value="">Select a primary type...</option>
                <option value="false">Open (jungle) primary</option>
                <option value="true">Closed party primary</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Louisiana's Public Service Commission, Supreme Court and BESE candidates run a
                closed party primary and file under Form 102's party-primary section. Every other
                office runs the open (jungle) primary. Required before you can generate a Louisiana
                report.
              </p>
            </div>
          )}

          {/* Federal-only and OPTIONAL: a committee that leaves it unset keeps the ceilings it
              already had. Senate is the one that changes anything today. */}
          {isFederalRuleset && (
            <div className="mt-4">
              <label htmlFor="finance-federalChamber" className="text-sm font-medium text-muted-foreground">
                Federal Office
              </label>
              <select
                id="finance-federalChamber"
                value={form.federalChamber}
                onChange={(e) => setForm((prev) => ({ ...prev, federalChamber: e.target.value }))}
                className={cn(inputClass, 'max-w-xs')}
              >
                <option value="">Select an office...</option>
                {FEDERAL_CHAMBER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Sets which limits apply to money from party committees — a U.S. Senate
                candidate's is far higher than a House candidate's. Leave this blank and party
                money is checked against the House limit.
              </p>

              {/* 52 U.S.C. § 30116(h). Shown only where the recorded chamber actually has a
                  combined ceiling — a House or presidential committee, and one with no chamber
                  recorded, has none, and printing the ordinary figure under this heading would
                  read as the combined one and understate it roughly twelvefold. The two render
                  together on purpose: a second bare "party" number beside the first teaches a
                  treasurer nothing, so each says who it covers. */}
              {chamberCeilingsAreCurrent && combinedNationalPartyCeiling !== null && (
                <dl className="mt-3 space-y-2 rounded-md border border-border bg-muted/30 p-3">
                  {partyCeiling !== null && (
                    <div data-testid="party-ceiling">
                      <dt className="text-xs font-medium">
                        Party committee — {formatAmount(partyCeiling)}
                      </dt>
                      <dd className="text-xs text-muted-foreground">
                        What one party committee may give per election, including a state,
                        district or local one.
                      </dd>
                    </div>
                  )}
                  <div data-testid="national-party-combined-ceiling">
                    <dt className="text-xs font-medium">
                      National and senatorial committees, combined —{' '}
                      {formatAmount(combinedNationalPartyCeiling)}
                    </dt>
                    <dd className="text-xs text-muted-foreground">
                      Your party's national committee and its senatorial campaign committee
                      share this ceiling for your race — it is one ceiling between the two of
                      them, not one each.
                    </dd>
                  </div>
                </dl>
              )}

              {/* Figures that vanish without a word are their own small lie — a treasurer who
                  changed the picker is left unsure whether this office simply has no ceilings.
                  Say why instead. Deliberately promises nothing about WHAT will appear: the new
                  office may have no combined ceiling, in which case saving correctly shows
                  nothing, and copy naming a figure would have over-promised. */}
              {!chamberCeilingsAreCurrent && (
                <p
                  data-testid="party-ceilings-pending-save"
                  className="text-xs text-muted-foreground mt-3"
                >
                  Party ceilings update when you save.
                </p>
              )}
            </div>
          )}

          {/* §163-278.40B–E — an NC municipal committee's reporting schedule is method-specific
              (there is no single municipal calendar), so the treasurer declares which election
              their city runs. The resulting report set is reviewable on the deadlines tab, which
              regenerates on save; leaving it unset surfaces a config-gap banner there. */}
          {isNcMunicipal && (
            <div className="mt-4">
              <label htmlFor="finance-municipalMethod" className="text-sm font-medium text-muted-foreground">
                Municipal Election Method
              </label>
              <select
                id="finance-municipalMethod"
                data-testid="municipal-method-select"
                value={form.municipalElectionMethod}
                onChange={(e) => setForm((prev) => ({ ...prev, municipalElectionMethod: e.target.value }))}
                className={cn(inputClass, 'max-w-xs')}
              >
                <option value="">Select an election method...</option>
                {MUNICIPAL_METHOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {form.municipalElectionMethod ? (
                <p className="text-xs text-muted-foreground mt-1">
                  {MUNICIPAL_METHOD_OPTIONS.find((o) => o.value === form.municipalElectionMethod)?.helper}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">
                  Determines which pre-election reports (35-day, pre-primary, pre-election, pre-runoff)
                  your city requires. Review the scheduled reports on the deadlines tab after saving.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Committee/Filer ID — every jurisdiction including federal (FEC-011:
            the FEC committee ID C00… rides StateFilerId, the unified filer-ID
            concept the builder reads; per-jurisdiction label below). FIN-011. */}
        {form.rulesetCode && (
          <div>
            <label htmlFor="finance-stateFilerId" className="text-sm font-medium text-muted-foreground">
              {stateFilerIdLabel(form.rulesetCode)}
            </label>
            <input
              id="finance-stateFilerId"
              data-testid="state-filer-id-input"
              type="text"
              value={form.stateFilerId}
              onChange={(e) => setForm((prev) => ({ ...prev, stateFilerId: e.target.value }))}
              placeholder="Enter your committee/filer ID"
              className={cn(inputClass, 'max-w-xs')}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Required to file electronic submissions.
            </p>
          </div>
        )}

        {/* #860 — filing-destination email override. Shown only for an email-transport jurisdiction
            (NC, MS): the app emails / the treasurer self-emails the report to a receiving-office
            inbox. The override wins over the jurisdiction default and is remembered between filings;
            a county Mississippi filer, who has no default at all, supplies their Circuit Clerk here. */}
        {filesByEmail && (
          <div>
            <label htmlFor="finance-filingDestinationEmail" className="text-sm font-medium text-muted-foreground">
              Filing destination email
            </label>
            <input
              id="finance-filingDestinationEmail"
              data-testid="filing-destination-email-input"
              type="email"
              value={form.filingDestinationEmail}
              onChange={(e) => setForm((prev) => ({ ...prev, filingDestinationEmail: e.target.value }))}
              placeholder={defaultDestination ?? 'Where your report is emailed'}
              className={cn(inputClass, 'max-w-xs')}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {complianceProfile?.portalName
                ? `The email address your report is filed to (${complianceProfile.portalName}).`
                : 'The email address your report is filed to.'}{' '}
              {defaultDestination
                ? `Leave blank to file to ${defaultDestination}.`
                : savedJurisdictionMatchesForm
                  ? "There's no default inbox for this office, so without an address you'll download the report and file it yourself."
                  : 'Leave blank to use the default where your jurisdiction provides one.'}
            </p>
          </div>
        )}

        {/* Committee Mailing Address (FEC-002) — FEC cover only, so ADR-022
            renders it for the federal jurisdiction alone. */}
        {isFederalRuleset && (
        <div>
          <h3 className="text-sm font-semibold mb-3">Committee Mailing Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label htmlFor="finance-committeeStreet1" className="text-sm font-medium text-muted-foreground">
                Street Address
              </label>
              <input
                id="finance-committeeStreet1"
                type="text"
                value={form.committeeStreet1}
                onChange={(e) => setForm((prev) => ({ ...prev, committeeStreet1: e.target.value }))}
                placeholder="123 Main St"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="finance-committeeCity" className="text-sm font-medium text-muted-foreground">
                City
              </label>
              <input
                id="finance-committeeCity"
                type="text"
                value={form.committeeCity}
                onChange={(e) => setForm((prev) => ({ ...prev, committeeCity: e.target.value }))}
                className={inputClass}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="finance-committeeState" className="text-sm font-medium text-muted-foreground">
                  State
                </label>
                <input
                  id="finance-committeeState"
                  type="text"
                  maxLength={2}
                  value={form.committeeState}
                  onChange={(e) => setForm((prev) => ({ ...prev, committeeState: e.target.value.toUpperCase() }))}
                  placeholder="GA"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="finance-committeeZip" className="text-sm font-medium text-muted-foreground">
                  ZIP
                </label>
                <input
                  id="finance-committeeZip"
                  type="text"
                  value={form.committeeZip}
                  onChange={(e) => setForm((prev) => ({ ...prev, committeeZip: e.target.value }))}
                  placeholder="30301"
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Candidate — every jurisdiction reads the candidate (the personal-funds carve-out, FEC
            line 11d, Mississippi's Candidate Committee cover), so the picker renders for all of them.
            The election state/district fields are FEC-cover-only and stay federal-only inside. */}
        {form.rulesetCode && (
        <div>
          <h3 className="text-sm font-semibold mb-3">Candidate</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isFederalRuleset && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="finance-electionState" className="text-sm font-medium text-muted-foreground">
                  Election State
                </label>
                <input
                  id="finance-electionState"
                  type="text"
                  maxLength={2}
                  value={form.electionState}
                  onChange={(e) => setForm((prev) => ({ ...prev, electionState: e.target.value.toUpperCase() }))}
                  placeholder="GA"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="finance-electionDistrict" className="text-sm font-medium text-muted-foreground">
                  District
                </label>
                <input
                  id="finance-electionDistrict"
                  type="text"
                  maxLength={2}
                  value={form.electionDistrict}
                  onChange={(e) => setForm((prev) => ({ ...prev, electionDistrict: e.target.value }))}
                  placeholder="05"
                  className={inputClass}
                />
              </div>
            </div>
            )}
            <div>
              <label htmlFor="finance-candidateContact" className="text-sm font-medium text-muted-foreground">
                Candidate Contact
              </label>
              <div className="relative mt-1" ref={candidateRef}>
                <button
                  id="finance-candidateContact"
                  type="button"
                  role="combobox"
                  aria-label="Candidate Contact"
                  aria-expanded={candidateOpen}
                  className={cn(
                    inputClass,
                    'mt-0 flex items-center justify-between text-left cursor-pointer',
                    !form.candidateContactId && 'text-muted-foreground',
                  )}
                  onClick={() => setCandidateOpen((prev) => !prev)}
                >
                  <span>
                    {form.candidateDisplayName
                      || candidateContacts.find((c) => c.id === form.candidateContactId)?.name
                      || (form.candidateContactId ? 'Candidate on file' : 'Select candidate contact...')}
                  </span>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                </button>
                {candidateOpen && (
                  <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
                    <input
                      type="text"
                      value={candidateSearch}
                      onChange={(e) => setCandidateSearch(e.target.value)}
                      placeholder="Search contacts..."
                      className="w-full border-b px-3 py-2 text-sm outline-none"
                      autoFocus
                    />
                    <ul className="max-h-48 overflow-y-auto p-1">
                      {form.candidateContactId && (
                        <li>
                          <button
                            type="button"
                            className="w-full text-left px-2 py-1.5 text-sm rounded-sm text-muted-foreground hover:bg-accent"
                            onClick={() => {
                              setForm((prev) => ({ ...prev, candidateContactId: '', candidateDisplayName: '' }));
                              setCandidateOpen(false);
                              setCandidateSearch('');
                            }}
                          >
                            Clear candidate
                          </button>
                        </li>
                      )}
                      {candidateContacts.map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            className={cn(
                              'w-full text-left px-2 py-1.5 text-sm rounded-sm hover:bg-accent',
                              form.candidateContactId === c.id && 'bg-accent',
                            )}
                            onClick={() => {
                              // D5 amended — structural uniqueness: one FK overwritten.
                              setForm((prev) => ({
                                ...prev,
                                candidateContactId: c.id,
                                candidateDisplayName: c.name,
                              }));
                              setCandidateOpen(false);
                              setCandidateSearch('');
                            }}
                          >
                            {c.name}
                          </button>
                        </li>
                      ))}
                      {candidateContacts.length === 0 && (
                        <li
                          role={candidateContactsUnready ? 'alert' : undefined}
                          className="px-2 py-3 text-sm text-center text-muted-foreground"
                        >
                          {candidateContactsUnready ?? 'No contacts found.'}
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {isFederalRuleset
                  ? "The candidate's own contributions file as line 11d; loans identify them as lender."
                  : code === 'MS'
                    ? "Naming the candidate files your reports on the Candidate Committee form. Leave it blank only for a political committee."
                    : "Naming the candidate marks their own money as personal candidate funds, so it isn't counted against contribution limits."}
              </p>
            </div>
          </div>
        </div>
        )}

        {/* LA LEADERS CVR/CVR2 filer identity (design #172 §3) — the LEADERS cover needs the
            committee's bank (depository) address, the report preparer, and the office
            jurisdiction. Shown for the LA jurisdiction only. */}
        {isLouisianaRuleset && (
        <div>
          <h3 className="text-sm font-semibold mb-1">Report Cover Details</h3>
          <p className="text-xs text-muted-foreground mb-3">
            Louisiana's LEADERS report cover lists your committee's bank, who prepared the report,
            and the jurisdiction of the office you seek.
          </p>

          <p className="text-sm font-medium mb-2">Financial Institution</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label htmlFor="finance-fiName" className="text-sm font-medium text-muted-foreground">
                Financial institution name
              </label>
              <input
                id="finance-fiName"
                type="text"
                value={form.financialInstitutionName}
                onChange={(e) => setForm((prev) => ({ ...prev, financialInstitutionName: e.target.value }))}
                placeholder="e.g. Whitney Bank"
                className={inputClass}
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="finance-fiStreet" className="text-sm font-medium text-muted-foreground">
                Financial institution street
              </label>
              <input
                id="finance-fiStreet"
                type="text"
                value={form.financialInstitutionStreet}
                onChange={(e) => setForm((prev) => ({ ...prev, financialInstitutionStreet: e.target.value }))}
                placeholder="228 St Charles Ave"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="finance-fiCity" className="text-sm font-medium text-muted-foreground">
                Financial institution city
              </label>
              <input
                id="finance-fiCity"
                type="text"
                value={form.financialInstitutionCity}
                onChange={(e) => setForm((prev) => ({ ...prev, financialInstitutionCity: e.target.value }))}
                className={inputClass}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="finance-fiState" className="text-sm font-medium text-muted-foreground">
                  Financial institution state
                </label>
                <input
                  id="finance-fiState"
                  type="text"
                  value={form.financialInstitutionState}
                  onChange={(e) => setForm((prev) => ({ ...prev, financialInstitutionState: e.target.value }))}
                  placeholder="LA"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="finance-fiZip" className="text-sm font-medium text-muted-foreground">
                  Financial institution ZIP
                </label>
                <input
                  id="finance-fiZip"
                  type="text"
                  value={form.financialInstitutionZip}
                  onChange={(e) => setForm((prev) => ({ ...prev, financialInstitutionZip: e.target.value }))}
                  placeholder="70130"
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          <p className="text-sm font-medium mt-4 mb-2">Report Preparer</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="finance-preparerName" className="text-sm font-medium text-muted-foreground">
                Preparer name
              </label>
              <input
                id="finance-preparerName"
                type="text"
                value={form.preparerName}
                onChange={(e) => setForm((prev) => ({ ...prev, preparerName: e.target.value }))}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="finance-preparerPhone" className="text-sm font-medium text-muted-foreground">
                Preparer phone
              </label>
              <input
                id="finance-preparerPhone"
                type="tel"
                value={form.preparerPhone}
                onChange={(e) => setForm((prev) => ({ ...prev, preparerPhone: e.target.value }))}
                placeholder="504-555-0100"
                className={inputClass}
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="finance-officeJurisdiction" className="text-sm font-medium text-muted-foreground">
                Office jurisdiction
              </label>
              <input
                id="finance-officeJurisdiction"
                type="text"
                value={form.officeJurisdiction}
                onChange={(e) => setForm((prev) => ({ ...prev, officeJurisdiction: e.target.value }))}
                placeholder="e.g. Orleans Parish"
                className={inputClass}
              />
              <p className="text-xs text-muted-foreground mt-1">
                The parish or district that governs the office you're seeking.
              </p>
            </div>
          </div>

          <p className="text-sm font-medium mt-4 mb-2">Treasurer Mailing Address</p>
          <p className="text-xs text-muted-foreground mb-2">
            The treasurer's own address, printed in box 9 on page 2 of Form 102.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label htmlFor="finance-treasurerStreet1" className="text-sm font-medium text-muted-foreground">
                Treasurer street
              </label>
              <input
                id="finance-treasurerStreet1"
                type="text"
                value={form.treasurerStreet1}
                onChange={(e) => setForm((prev) => ({ ...prev, treasurerStreet1: e.target.value }))}
                placeholder="742 Evangeline St"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="finance-treasurerCity" className="text-sm font-medium text-muted-foreground">
                Treasurer city
              </label>
              <input
                id="finance-treasurerCity"
                type="text"
                value={form.treasurerCity}
                onChange={(e) => setForm((prev) => ({ ...prev, treasurerCity: e.target.value }))}
                className={inputClass}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="finance-treasurerState" className="text-sm font-medium text-muted-foreground">
                  Treasurer state
                </label>
                <input
                  id="finance-treasurerState"
                  type="text"
                  value={form.treasurerState}
                  onChange={(e) => setForm((prev) => ({ ...prev, treasurerState: e.target.value }))}
                  placeholder="LA"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="finance-treasurerZip" className="text-sm font-medium text-muted-foreground">
                  Treasurer ZIP
                </label>
                <input
                  id="finance-treasurerZip"
                  type="text"
                  value={form.treasurerZip}
                  onChange={(e) => setForm((prev) => ({ ...prev, treasurerZip: e.target.value }))}
                  placeholder="70501"
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Default Submission Format — controls file-report output for this
            campaign. ADR-022: the redundant Filing State picker is gone; the
            format options come from the sole jurisdiction (rulesetCode). */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="finance-defaultFormatKind" className="text-sm font-medium text-muted-foreground">
              Submission Format
            </label>
            <select
              id="finance-defaultFormatKind"
              className={inputClass}
              value={form.defaultFormatKind}
              onChange={(e) => setForm((prev) => ({ ...prev, defaultFormatKind: e.target.value }))}
              disabled={!form.rulesetCode || availableFormats.length === 0}
            >
              <option value="">— Select format —</option>
              {availableFormats.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Committee Formation Date (FIP-017) */}
        <div>
          <h3 className="text-sm font-semibold mb-3">Committee Formation</h3>
          <div>
            <label htmlFor="finance-formationDate" className="text-sm font-medium text-muted-foreground">
              Committee Formation Date
            </label>
            <input
              id="finance-formationDate"
              data-testid="formation-date-picker"
              type="date"
              value={form.committeeFormationDate}
              onChange={(e) => setForm((prev) => ({ ...prev, committeeFormationDate: e.target.value }))}
              className={cn(inputClass, 'max-w-xs')}
            />
            <p className="text-xs text-muted-foreground mt-1">
              The date your committee was officially formed or registered.
            </p>
          </div>

          {/* Mid-cycle cutoff: only file from the day after what's already filed. */}
          <div className="mt-4">
            <label htmlFor="finance-priorFilingThrough" className="text-sm font-medium text-muted-foreground">
              Already filed through (optional)
            </label>
            <input
              id="finance-priorFilingThrough"
              data-testid="prior-filing-through-picker"
              type="date"
              value={form.priorFilingThroughDate}
              onChange={(e) => setForm((prev) => ({ ...prev, priorFilingThroughDate: e.target.value }))}
              className={cn(inputClass, 'max-w-xs')}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Joining mid-cycle? If you've already filed your own reports up to a point, enter
              that date. We'll only schedule filings from the day after — earlier periods are yours.
            </p>
          </div>

          {existingSettings && (
            <div className="mt-4" data-testid="opening-balance-row">
              <span className="text-sm font-medium text-muted-foreground">Opening cash on hand</span>
              <div className="mt-1 flex items-center gap-3">
                <span className="text-sm">
                  {existingSettings.openingBalance != null && existingSettings.openingBalanceDate
                    ? `${formatAmount(existingSettings.openingBalance)} as of ${format(
                        parseISO(existingSettings.openingBalanceDate),
                        'MMM d, yyyy',
                      )}`
                    : 'Not set'}
                </span>
                <Button type="button" variant="outline" size="sm" onClick={() => openingBalanceDialog.open()}>
                  {existingSettings.openingBalance != null ? 'Edit opening cash' : 'Set opening cash'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                The cash you had when you started filing here, usually as of the date above. Your
                first report in the app starts from it.
              </p>
              {openingBalanceDialog.isOpen && (
                <OpeningBalanceModal
                  open
                  onOpenChange={(open) => !open && openingBalanceDialog.close()}
                  existingBalance={existingSettings.openingBalance}
                  existingDate={existingSettings.openingBalanceDate}
                  concurrencyVersion={existingSettings.concurrencyVersion}
                  onSaved={adoptOpeningBalanceToken}
                />
              )}
            </div>
          )}

          {/* Missed deadline warning */}
          {existingSettings?.committeeFormationDate && missedDeadlines && missedDeadlines.length > 0 && (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                Your committee was formed on {existingSettings.committeeFormationDate}. You may have missed {missedDeadlines.length} prior filing {missedDeadlines.length === 1 ? 'deadline' : 'deadlines'}.
              </div>
              <ul className="mt-2 space-y-1">
                {missedDeadlines.map((d) => (
                  <li key={`${d.name}-${d.periodStart}`} className="flex items-center justify-between text-sm">
                    <span className="text-amber-700">
                      {d.name} ({d.periodStart} – {d.periodEnd}), due {d.dueDate}
                    </span>
                    <button
                      type="button"
                      className="text-xs text-amber-800 underline hover:text-amber-900"
                      onClick={() => createAttestation({
                        scheduleName: d.name,
                        periodStart: d.periodStart,
                        periodEnd: d.periodEnd,
                      })}
                    >
                      Filed elsewhere
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Election Dates */}
        <div>
          <h3 className="text-sm font-semibold mb-3">Election Dates</h3>
          <p className="text-xs text-muted-foreground mb-3">
            Check which elections your campaign is participating in. At least one is required.
          </p>
          <div className="space-y-3">
            {campaignRace && raceDatesDiffer && (
              <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/40 p-3">
                <p className="text-sm">
                  Your race, {campaignRace.raceName}, votes on {raceDateWords}.
                </p>
                <Button type="button" size="sm" variant="outline" onClick={fillRaceDates}>
                  Use race dates
                </Button>
              </div>
            )}
            {/* Primary */}
            <div className="flex items-center gap-3">
              <input
                id="finance-primaryEnabled"
                type="checkbox"
                checked={form.primaryEnabled}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    primaryEnabled: e.target.checked,
                    primaryDate: e.target.checked ? prev.primaryDate : '',
                  }))
                }
                className="h-4 w-4 rounded border-input"
              />
              <label htmlFor="finance-primaryEnabled" className="text-sm font-medium">
                Primary
              </label>
              {form.primaryEnabled && (
                <input
                  data-testid="primary-date-picker"
                  type="date"
                  value={form.primaryDate}
                  onChange={(e) => setForm((prev) => ({ ...prev, primaryDate: e.target.value }))}
                  className={cn(inputClass, 'w-auto mt-0')}
                />
              )}
              {form.primaryEnabled && !form.primaryDate && statutoryDates?.primaryDate && statutoryDates.primaryDate !== racePrimary && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setForm((prev) => ({ ...prev, primaryDate: statutoryDates.primaryDate ?? '' }))
                  }
                >
                  Use {format(parseISO(statutoryDates.primaryDate), 'MMMM d, yyyy')}
                </Button>
              )}
            </div>
            {form.primaryEnabled && primaryWarning && (
              <p className="flex items-center gap-2 text-xs text-amber-700">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {primaryWarning}
              </p>
            )}

            {/* General */}
            <div className="flex items-center gap-3">
              <input
                id="finance-generalEnabled"
                type="checkbox"
                checked={form.generalEnabled}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    generalEnabled: e.target.checked,
                    generalDate: e.target.checked ? prev.generalDate : '',
                  }))
                }
                className="h-4 w-4 rounded border-input"
              />
              <label htmlFor="finance-generalEnabled" className="text-sm font-medium">
                General
              </label>
              {form.generalEnabled && (
                <input
                  data-testid="general-date-picker"
                  type="date"
                  value={form.generalDate}
                  onChange={(e) => setForm((prev) => ({ ...prev, generalDate: e.target.value }))}
                  className={cn(inputClass, 'w-auto mt-0')}
                />
              )}
              {form.generalEnabled && !form.generalDate && statutoryDates?.generalDate && statutoryDates.generalDate !== raceGeneral && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setForm((prev) => ({ ...prev, generalDate: statutoryDates.generalDate ?? '' }))
                  }
                >
                  Use {format(parseISO(statutoryDates.generalDate), 'MMMM d, yyyy')}
                </Button>
              )}
            </div>
            {form.generalEnabled && generalWarning && (
              <p className="flex items-center gap-2 text-xs text-amber-700">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {generalWarning}
              </p>
            )}

            {/* Primary runoff — precedes the general (ADR-076). */}
            <div className="flex items-center gap-3">
              <input
                id="finance-primaryRunoffEnabled"
                type="checkbox"
                checked={form.primaryRunoffEnabled}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    primaryRunoffEnabled: e.target.checked,
                    primaryRunoffDate: e.target.checked ? prev.primaryRunoffDate : '',
                  }))
                }
                className="h-4 w-4 rounded border-input"
              />
              <label htmlFor="finance-primaryRunoffEnabled" className="text-sm font-medium">
                Primary runoff
              </label>
              {form.primaryRunoffEnabled && (
                <input
                  data-testid="primary-runoff-date-picker"
                  type="date"
                  value={form.primaryRunoffDate}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, primaryRunoffDate: e.target.value }))
                  }
                  className={cn(inputClass, 'w-auto mt-0')}
                />
              )}
            </div>

            {/* General runoff — follows the general; the runoff scheduled only after it (ADR-076). */}
            <div className="flex items-center gap-3">
              <input
                id="finance-generalRunoffEnabled"
                type="checkbox"
                checked={form.generalRunoffEnabled}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    generalRunoffEnabled: e.target.checked,
                    generalRunoffDate: e.target.checked ? prev.generalRunoffDate : '',
                  }))
                }
                className="h-4 w-4 rounded border-input"
              />
              <label htmlFor="finance-generalRunoffEnabled" className="text-sm font-medium">
                General runoff
              </label>
              {form.generalRunoffEnabled && (
                <input
                  data-testid="general-runoff-date-picker"
                  type="date"
                  value={form.generalRunoffDate}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, generalRunoffDate: e.target.value }))
                  }
                  className={cn(inputClass, 'w-auto mt-0')}
                />
              )}
            </div>
          </div>

          {/* ADR-037 — stored-cycle (VA, NC, LA, NJ) election-cycle start. These jurisdictions total
              each donor over the whole election cycle (not the calendar year), so this anchors the
              window / the cumulative-cycle figures. Gated on the DEDICATED persistsCycleStartAnchor
              flag (not isStoredCycleRuleset, which is VA-only and also drives VA office-sought /
              district / the session-ban). Jurisdiction-neutral copy — no "Virginia adds up…" text.
              WHAT the date means differs: VA/NC take January 1 of the year the candidate first
              sought the office (and get a Jan-1 shortcut); LA takes the candidacy date and NJ the
              committee's first activity, so each gets its own sentence and NO shortcut. */}
          {persistsCycleStartAnchor && (
            <div className="mt-4 space-y-2">
              <label htmlFor="finance-electionCycleStart" className="text-sm font-medium">
                Election-cycle start
              </label>
              <p className="text-xs text-muted-foreground">
                {cycleStartIsJanuaryFirst ? (
                  <>
                    This state totals each donor&apos;s giving over the whole election cycle — not
                    the calendar year — to fill in your cumulative-cycle figures. This is January 1
                    of the year you first sought this office (earlier for a multi-year term or a
                    re-election).
                  </>
                ) : code === 'NJ' ? (
                  <>
                    New Jersey totals each donor&apos;s giving per election, starting from your
                    committee&apos;s first contribution or expenditure. This is the date you first
                    accepted a contribution or made a campaign expenditure.
                  </>
                ) : (
                  <>
                    Louisiana totals each donor&apos;s giving from the day you became a candidate
                    through the end of each report — not by calendar year — to fill in the “Total
                    this Election Cycle” column beside every contribution. This is the date you
                    first accepted a contribution, made a campaign expenditure, or qualified for the
                    ballot — whichever came first.
                  </>
                )}
              </p>
              <div className="flex items-center gap-2">
                <input
                  id="finance-electionCycleStart"
                  data-testid="election-cycle-start-picker"
                  type="date"
                  value={form.electionCycleStartDate}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, electionCycleStartDate: e.target.value }))
                  }
                  className={cn(inputClass, 'w-auto mt-0')}
                />
                {!form.electionCycleStartDate && suggestedCycleStart && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setForm((prev) => ({ ...prev, electionCycleStartDate: suggestedCycleStart }))
                    }
                  >
                    Use Jan 1, {suggestedCycleStart.slice(0, 4)}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* VA COMET header identity fields — appear on every generated report. VA-only
              (isStoredCycleRuleset); NC does not use them, so they stay keyed to VA and are not
              dragged on by the shared cycle-start anchor above. */}
          {isStoredCycleRuleset && (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <label htmlFor="finance-officeSought" className="text-sm font-medium">
                  Office sought
                </label>
                <p className="text-xs text-muted-foreground">
                  The office you're running for, e.g. "Governor" or "Member, House of Delegates".
                </p>
                <input
                  id="finance-officeSought"
                  type="text"
                  value={form.officeSought}
                  onChange={(e) => setForm((prev) => ({ ...prev, officeSought: e.target.value }))}
                  className={cn(inputClass, 'mt-0')}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="finance-district" className="text-sm font-medium">
                  District
                </label>
                <p className="text-xs text-muted-foreground">
                  Your district, e.g. "District 4". Leave blank if you're running statewide.
                </p>
                <input
                  id="finance-district"
                  type="text"
                  value={form.district}
                  onChange={(e) => setForm((prev) => ({ ...prev, district: e.target.value }))}
                  className={cn(inputClass, 'mt-0')}
                />
              </div>
            </div>
          )}

          {/* ADR-040 (§24.2-954) — legislative-session fundraising ban: statewide + General
              Assembly committees only. The session start is computed automatically; the treasurer
              confirms the adjournment date and marks a special-election candidacy. */}
          {isVaSessionBanOffice && (
            <div className="mt-4 space-y-3 rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-medium">Legislative-session fundraising ban</p>
                <p className="text-xs text-muted-foreground">
                  Virginia bars accepting contributions while the General Assembly is in session
                  (§ 24.2-954). We flag in-session gifts automatically — the session start is
                  computed for you.
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="finance-sineDie" className="text-sm font-medium">
                  Session adjournment date (sine die)
                </label>
                <p className="text-xs text-muted-foreground">
                  Confirm the date the session actually adjourned so the ban window closes on the
                  real date. Until you set it, we keep flagging in-session gifts to be safe.
                </p>
                <input
                  id="finance-sineDie"
                  data-testid="legislative-session-end-picker"
                  type="date"
                  value={form.legislativeSessionEndConfirmed}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, legislativeSessionEndConfirmed: e.target.value }))
                  }
                  className={cn(inputClass, 'w-auto mt-0')}
                />
              </div>

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="special-election-committee-checkbox"
                  checked={form.isSpecialElectionCommittee}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, isSpecialElectionCommittee: e.target.checked }))
                  }
                  className="mt-1"
                />
                <span>
                  <span className="font-medium">This is a special-election committee</span>
                  <span className="block text-xs text-muted-foreground">
                    Special-election candidates are exempt from the session ban, so we won't flag
                    in-session gifts while this is checked.
                  </span>
                </span>
              </label>
            </div>
          )}

          {/* NC § 163-278.9(a) — the under-$1,000 Certification of Threshold. A committee that will
              keep total activity under $1,000 files the certification with its organizational report
              and then owes no periodic reports. State/county candidate committees only. */}
          {isNcThresholdEligibleOffice && (
            <div className="mt-4 space-y-3 rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-medium">Under-$1,000 threshold committee</p>
                <p className="text-xs text-muted-foreground">
                  North Carolina lets a committee that will keep total activity under $1,000 for the
                  cycle file a Certification of Threshold with its organizational report and then skip
                  the periodic quarterly / semiannual reports (§ 163-278.9(a)).
                </p>
              </div>

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="threshold-committee-checkbox"
                  checked={form.isThresholdCommittee}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, isThresholdCommittee: e.target.checked }))
                  }
                  className="mt-1"
                />
                <span>
                  <span className="font-medium">This committee will stay under $1,000</span>
                  <span className="block text-xs text-muted-foreground">
                    We'll schedule the Certification of Threshold instead of the quarterly and
                    semiannual reports. If activity later crosses $1,000, clear this to resume the
                    regular reports — the 48-hour rapid-report reminder always stays on.
                  </span>
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleSave} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                Saving...
              </>
            ) : (
              'Save Finance Settings'
            )}
          </Button>
          {saveSuccess && (
            <span className="flex items-center gap-1 text-sm text-green-600">
              <Check className="h-4 w-4" />
              Saved
            </span>
          )}
          {saveFailure && (
            // Surface the server's own message (the treasurer-facing ValidationException
            // copy, e.g. "At least one election date is required." or the formation-date
            // conflict) so the treasurer sees WHY the save failed — never a bare generic
            // string that hides an actionable reason. Fall back to the generic copy only
            // when the error carries no text (e.g. a network failure).
            <span className="text-sm text-destructive">
              {saveFailure.message.trim() || 'Failed to save. Please try again.'}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
